import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@ublend-npm/aulaui-core';
import type { AulaState } from '@core/types/state';
import {
  operations as reactionOperations,
  selectors as reactionSelectors,
} from '@app/store/reactions';
import Reaction from './Reaction';
import type { EmojiReaction, ReactionItemType } from './types';

type GetReactionsSelector = (
  state: AulaState,
  props: { itemId: string }
) => EmojiReaction[];

type ReactionListProps = {
  itemId: string;
  itemType: ReactionItemType;
  readOnly?: boolean;
  hideWhenNoReactions?: boolean;
  onRefetch?: () => void | Promise<void>;
  sx?: { [key: string]: any };
};

const ReactionList = ({
  itemId,
  itemType,
  readOnly = false,
  hideWhenNoReactions = false,
  onRefetch,
  sx,
}: ReactionListProps) => {
  const dispatch = useDispatch();
  const getReactions = useMemo<GetReactionsSelector>(() => {
    const selector: any = reactionSelectors.getReactionsSelector();
    return (state, props) => selector(state, props);
  }, []);
  const reactions = useSelector<AulaState, EmojiReaction[]>((state) =>
    getReactions(state, { itemId })
  );

  const toggleReaction = useCallback(
    async (emojiName: string) => {
      await dispatch(
        reactionOperations.toggleReaction({ emojiName, itemId, itemType })
      );
      await onRefetch?.();
    },
    [dispatch, itemId, itemType, onRefetch]
  );

  if (!itemId || (hideWhenNoReactions && reactions.length === 0)) return null;

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        ...sx,
      }}
    >
      {reactions.map((reaction) => (
        <Reaction
          key={reaction.id}
          reaction={reaction}
          itemId={itemId}
          readOnly={readOnly}
          onToggle={toggleReaction}
        />
      ))}
    </Box>
  );
};

export default ReactionList;
